import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Phone } from 'lucide-react';
import { Button } from './ui/button';
import Nis2Widget from './Nis2Widget';

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const [widgetOpen, setWidgetOpen] = useState(false);
  const location = useLocation();

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path) && location.pathname !== '/';
  };

  const infoLinks = [
    { to: '/nis2', label: 'NIS2 přehled' },
    { to: '/nis2-koho-se-tyka', label: 'Koho se týká' },
    { to: '/povinnosti-nis2', label: 'Povinnosti' },
    { to: '/priprava-na-nis2', label: 'Příprava' },
    { to: '/vyhody-nis2', label: 'Výhody' },
    { to: '/kyberneticke-incidenty', label: 'Kybernetické incidenty' },
    { to: '/nis2-smernice', label: 'Směrnice EU' },
    { to: '/blog', label: 'Blog' },
    { to: '/ebook-1', label: 'Základní E-book' },
    { to: '/ebook-2', label: 'E-book NIS2 rozšíření' },
  ];

  const resourcesLinks = [
    { to: '/nis2-smernice-kyberneticka-bezpecnost-eu', label: 'Co je NIS2 směrnice?' },
    { to: '/webinar', label: 'NIS2 od A do Z' },
    { to: '#newsletter', label: 'Newsletter o NIS2' },
  ];

  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <nav className={`bg-white sticky top-0 z-50 border-b border-gray-100 transition-shadow duration-300 ${
        isScrolled ? 'shadow-sm' : ''
      }`}>
        <div className="container-custom">
          <div className="flex items-center justify-between h-[73.6px] sm:h-[82.8px] md:h-[92px] py-[18.4px]">
            {/* Logo */}
            <Link 
              to="/" 
              onClick={(e) => {
                if (location.pathname === '/') {
                  e.preventDefault();
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                } else {
                  // Pokud jsme na jiné stránce, necháme React Router přejít na homepage
                  // a pak scrollujeme na vrch pomocí useEffect v Homepage komponentě
                }
              }}
              className="flex items-center space-x-2 flex-shrink-0"
              aria-label="NIS2 Ochrana - Domovská stránka"
            >
              <img src="/Logotype.svg" alt="NIS2 Ochrana" className="h-[46px] sm:h-[55.2px] md:h-[55.2px]" loading="eager" />
            </Link>

            {/* Desktop Menu - Centered */}
            <div className="hidden md:flex items-center justify-center flex-1 space-x-[27.6px]">
              <div 
                className="relative"
                onMouseEnter={() => setInfoOpen(true)}
                onMouseLeave={() => setInfoOpen(false)}
              >
                <button
                  className={`text-[18.4px] font-medium transition-colors flex items-center gap-1 whitespace-nowrap ${
                    infoLinks.some(link => location.pathname === link.to) 
                      ? 'text-[#2A4CFF]' 
                      : 'text-gray-700 hover:text-[#2A4CFF]'
                  }`}
                  aria-label="O NIS2 - rozbalit menu"
                  aria-expanded={infoOpen}
                  aria-haspopup="true"
                >
                  O NIS2
                  <ChevronDown className="w-[18.4px] h-[18.4px] sm:w-[23px] sm:h-[23px] flex-shrink-0" aria-hidden="true" />
                </button>
                
                {infoOpen && (
                  <div 
                    className="absolute top-full left-0 mt-0 w-56 bg-white section-rounded shadow-lg border border-gray-200 py-2 animate-in fade-in slide-in-from-top-1 duration-200"
                    role="menu"
                    aria-label="O NIS2 submenu"
                  >
                    {infoLinks.map((link) => (
                      <Link
                        key={link.to}
                        to={link.to}
                        className="block px-4 py-2 text-[18.4px] text-gray-700 hover:bg-[#FFF8EA] hover:text-[#2A4CFF] transition-colors"
                        onClick={() => setInfoOpen(false)}
                      >
                        {link.label}
                      </Link>
                    ))}
                    <div className="border-t border-gray-200 my-2"></div>
                    <div className="px-4 py-2 text-[16px] font-semibold text-gray-900">Zdroje pro vaši přípravu</div>
                    {resourcesLinks.map((link) => (
                      <Link
                        key={link.to}
                        to={link.to}
                        className="block px-4 py-2 text-[18.4px] text-gray-700 hover:bg-[#FFF8EA] hover:text-[#2A4CFF] transition-colors"
                        onClick={() => setInfoOpen(false)}
                      >
                        {link.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              <Link
                to="/pricing"
                className={`text-[18.4px] font-medium transition-colors whitespace-nowrap ${
                  isActive('/pricing') || isActive('/pricing_desktop') ? 'text-[#2A4CFF]' : 'text-gray-700 hover:text-[#2A4CFF]'
                }`}
              >
                Ceník
              </Link>

              <Link
                to="/o-nas"
                className={`text-[18.4px] font-medium transition-colors whitespace-nowrap ${
                  isActive('/o-nas') ? 'text-[#2A4CFF]' : 'text-gray-700 hover:text-[#2A4CFF]'
                }`}
              >
                O nás
              </Link>

              <Link
                to="/kontakt"
                className={`text-[18.4px] font-medium transition-colors whitespace-nowrap ${
                  isActive('/kontakt') ? 'text-[#2A4CFF]' : 'text-gray-700 hover:text-[#2A4CFF]'
                }`}
              >
                Kontakt
              </Link>

              <Link
                to="/webinar"
                className={`text-[18.4px] font-medium transition-colors whitespace-nowrap ${
                  isActive('/webinar') ? 'text-[#2A4CFF]' : 'text-gray-700 hover:text-[#2A4CFF]'
                }`}
              >
                Webinář
              </Link>

              <Link
                to="/aplikace"
                className={`text-[18.4px] font-medium transition-colors whitespace-nowrap ${
                  isActive('/aplikace') ? 'text-[#2A4CFF]' : 'text-gray-700 hover:text-[#2A4CFF]'
                }`}
              >
                Aplikace
              </Link>
            </div>

            {/* Right Side - Phone & CTA */}
            <div className="hidden md:flex items-center space-x-[27.6px] flex-shrink-0">
              <a 
                href="tel:+420777963653" 
                className="text-[18.4px] font-medium text-gray-700 hover:text-[#2A4CFF] transition-colors whitespace-nowrap flex items-center gap-2"
                aria-label="Zavolat na telefonní číslo +420 777 963 653"
              >
                <Phone className="w-4 h-4" aria-hidden="true" />
                +420 777 963 653
              </a>

              <Button
                onClick={() => setWidgetOpen(true)}
                data-widget-trigger
                className="bg-[#F5B300] hover:bg-[#d49a00] text-[#0A0F1F] btn-rounded px-[27.6px] font-semibold text-[18px] whitespace-nowrap"
                aria-label="Otevřít widget pro zjištění, zda se vás týká NIS2"
              >
                Týká se mě?
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="md:hidden text-gray-700 p-2 -mr-2"
              aria-label={isOpen ? "Zavřít menu" : "Otevřít menu"}
              aria-expanded={isOpen}
              aria-controls="mobile-menu"
            >
              {isOpen ? (
                <X className="w-[32.2px] h-[32.2px] sm:w-[36.8px] sm:h-[36.8px]" aria-hidden="true" />
              ) : (
                <Menu className="w-[32.2px] h-[32.2px] sm:w-[36.8px] sm:h-[36.8px]" aria-hidden="true" />
              )}
            </button>
          </div>

          {/* Mobile Menu */}
          {isOpen && (
            <div 
              id="mobile-menu"
              className="md:hidden py-[18.4px] px-4 space-y-3 border-t border-gray-200 max-h-[calc(100vh-92px)] overflow-y-auto animate-in slide-in-from-top-2 duration-200"
              role="menu"
              aria-label="Hlavní navigace"
            >
              <div className="space-y-2 pt-2">
                <div className="text-[18.4px] font-medium text-gray-900 px-2 py-1">O NIS2</div>
                {infoLinks.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className="block pl-6 pr-2 py-2 text-[18.4px] text-gray-600 hover:text-[#2A4CFF] transition-colors"
                    onClick={() => setIsOpen(false)}
                  >
                    {link.label}
                  </Link>
                ))}
                <div className="text-[16px] font-semibold text-gray-900 px-2 py-1 mt-3">Zdroje pro vaši přípravu</div>
                {resourcesLinks.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className="block pl-6 pr-2 py-2 text-[18.4px] text-gray-600 hover:text-[#2A4CFF] transition-colors"
                    onClick={() => setIsOpen(false)}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>

              <Link
                to="/pricing"
                className="block text-[18.4px] font-medium text-gray-700 hover:text-[#2A4CFF] transition-colors py-2"
                onClick={() => setIsOpen(false)}
              >
                Ceník
              </Link>

              <Link
                to="/o-nas"
                className="block text-[18.4px] font-medium text-gray-700 hover:text-[#2A4CFF] transition-colors py-2"
                onClick={() => setIsOpen(false)}
              >
                O nás
              </Link>

              <Link
                to="/kontakt"
                className="block text-[18.4px] font-medium text-gray-700 hover:text-[#2A4CFF] transition-colors py-2"
                onClick={() => setIsOpen(false)}
              >
                Kontakt
              </Link>

              <Link
                to="/webinar"
                className="block text-[18.4px] font-medium text-gray-700 hover:text-[#2A4CFF] transition-colors py-2"
                onClick={() => setIsOpen(false)}
              >
                Webinář
              </Link>

              <Link
                to="/aplikace"
                className="block text-[18.4px] font-medium text-gray-700 hover:text-[#2A4CFF] transition-colors py-2"
                onClick={() => setIsOpen(false)}
              >
                Aplikace
              </Link>

              <a 
                href="tel:+420777963653" 
                className="block text-[18.4px] font-medium text-gray-700 hover:text-[#2A4CFF] transition-colors py-2 flex items-center gap-2"
                onClick={() => setIsOpen(false)}
                aria-label="Zavolat na telefonní číslo +420 777 963 653"
              >
                <Phone className="w-4 h-4" aria-hidden="true" />
                +420 777 963 653
              </a>

              <div className="pt-2 space-y-3">
                <Button
                  onClick={() => {
                    setWidgetOpen(true);
                    setIsOpen(false);
                  }}
                  className="bg-[#F5B300] hover:bg-[#d49a00] text-[#0A0F1F] btn-rounded w-full font-semibold text-[18px] py-3"
                  aria-label="Otevřít widget pro zjištění, zda se vás týká NIS2"
                >
                  Týká se mě?
                </Button>

                <Link to="/kontakt" className="w-full block">
                  <Button className="bg-[#2A4CFF] hover:bg-[#1e3acc] text-white btn-rounded w-full text-[18px] py-3">
                    Kontakt
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* NIS2 Widget */}
      <Nis2Widget isOpen={widgetOpen} onClose={() => setWidgetOpen(false)} />
    </>
  );
};